import { useMutation, useQuery } from "@tanstack/react-query";
import axiosConfig from "../axiosInstance";

export const useGetBusinesses = () =>
  useQuery({
    queryKey: ["getBusinesses"],
    queryFn: async () => await axiosConfig.get("/business"),
  });

export const useGetMerchantBusinesses = (merchantId, filters, enabled) =>
  useQuery({
    queryKey: ["getMerchantBusinesses", merchantId, { ...filters }],
    queryFn: async () =>
      await axiosConfig.get("/business/getAll/merchant", {
        params: { merchantId, ...filters },
      }),
    enabled: !!merchantId && enabled,
  });

export const useGetSubmerchantBusinesses = (submerchantId, enabled) =>
  useQuery({
    queryKey: ["getSubmerchantBusinesses", submerchantId],
    queryFn: async () =>
      await axiosConfig.get(`/merchant/users/businesses/${submerchantId}`),
    enabled: !!submerchantId && enabled,
  });

export const useAddBusiness = () =>
  useMutation({
    mutationKey: ["addBusiness"],
    mutationFn: async (formData) =>
      await axiosConfig.post("/business", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
  });

export const useCreateBusinessKyb = (businessId) =>
  useMutation({
    mutationKey: ["craeteBusinessKyb"],
    mutationFn: async (formData) =>
      await axiosConfig.post(
        `/business/KYB/fill/upload-Docs/${businessId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      ),
  });

export const useGetBusinessKybRecord = (businessId, enabled) => {
  const query = useQuery({
    queryKey: ["getBusinessKybRecord", businessId],
    queryFn: async () =>
      await axiosConfig.get(`/business/KYB/fill/${businessId}`),
    enabled: !!businessId && enabled,
  });

  return enabled ? query : { data: null, isPending: false, error: null };
};

export const useEditRejectedKyb = (id) =>
  useMutation({
    mutationKey: ["editRejectedKyb", id],
    mutationFn: async (formData) =>
      await axiosConfig.put(
        `/business/KYB/fill/upload-Docs/update/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      ),
  });

export const useEditBusiness = (id) =>
  useMutation({
    mutationKey: ["editBusiness"],
    mutationFn: async (formData) =>
      await axiosConfig.put(`/business/${id}`, formData, {
        // headers: {
        //   "Content-Type": "multipart/form-data",
        // },
      }),
  });

export const useGetSingleBusiness = (id) =>
  useQuery({
    queryKey: ["getSingleBusiness", id],
    queryFn: async () => await axiosConfig.get(`/business/${id}`),
  });

export const useDeleteBusiness = () =>
  useMutation({
    mutationKey: ["deleteBusiness"],
    mutationFn: async (id) => await axiosConfig.delete(`/business/${id}`),
  });

export const useEnableBusiness = () =>
  useMutation({
    mutationKey: ["enableBusiness"],
    mutationFn: async (id) => await axiosConfig.post(`/business/enable/${id}`),
  });

export const useDisableBusiness = () =>
  useMutation({
    mutationKey: ["disableBusiness"],
    mutationFn: async (id) => await axiosConfig.post(`/business/disable/${id}`),
  });

export const useGetBusinessCategories = () =>
  useQuery({
    queryKey: ["getBusinessCategories"],
    queryFn: async () => await axiosConfig.get("/businessCategory"),
  });

export const useUpdateBusinessLogo = (id) =>
  useMutation({
    mutationKey: ["updateBusinessLogo", id],
    mutationFn: async (formData) =>
      await axiosConfig.post(`/business/update/logo/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
  });

export const useAddCategory = () =>
  useMutation({
    mutationKey: ["addCategory"],
    mutationFn: async (formData) =>
      await axiosConfig.post("/category", formData),
  });

export const useEditCategory = (id) =>
  useMutation({
    mutationKey: ["editCategory"],
    mutationFn: async (formData) =>
      await axiosConfig.put(`/category/${id}`, formData),
  });

export const useGetSingleCategory = (id) =>
  useQuery({
    queryKey: ["getSingleCategory", id],
    queryFn: async () => await axiosConfig.get(`/category/${id}`),
  });

export const useDeleteCategory = () =>
  useMutation({
    mutationKey: ["deleteCategory"],
    mutationFn: async (id) => await axiosConfig.delete(`/category/${id}`),
  });

export const useGetBusinessTransactions = (id, filters) =>
  useQuery({
    queryKey: ["getBusinessTransactions", id, { ...filters }],
    queryFn: async () =>
      await axiosConfig.get(`/business/transactions/${id}`, {
        params: filters,
      }),

    // refetchInterval: 30000,
    keepPreviousData: true,
  });

export const useGetWalletTransactions = (id, filters) =>
  useQuery({
    queryKey: ["getBusinessTransactions", id, { ...filters }],
    queryFn: async () =>
      await axiosConfig.get(`/business/transactions/${id}`, {
        params: filters,
      }),

    refetchInterval: 10000,
    keepPreviousData: true,
  });

export const useGetBusinessWallet = (id) =>
  useQuery({
    queryKey: ["getBusinessWallet", id],
    queryFn: async () => await axiosConfig.get(`/business/wallet/${id}`),
    refetchInterval: 10000,
    keepPreviousData: true,
  });

export const useGetBusinessReports = (businessId, filters) =>
  useQuery({
    queryKey: [
      "getReports",
      businessId,
      {
        ...filters,
      },
    ],
    queryFn: async () =>
      await axiosConfig.get(`/business/report/${businessId}`, {
        params: filters,
      }),
  });

export const useSetDefaultBusiness = () =>
  useMutation({
    mutationKey: ["setDefaultBusiness"],
    mutationFn: async (id) => await axiosConfig.put(`/business/default/${id}`),
  });

export const useGetUsersAssigned = (businessId) =>
  useQuery({
    queryKey: ["getUsersAssigned", businessId],
    queryFn: async () =>
      await axiosConfig.get(`/merchant/business/users/${businessId}`),
  });

// export const useGetSubmerchantBusinesses = (submerchantId) =>
//   useQuery({
//     queryKey: ["getSubmerchantBusinesses", submerchantId],
//     queryFn: async () =>
//       await axiosConfig.get(`/merchant/users/businesses/${submerchantId}`),
//   });

export const useAssignToUser = () =>
  useMutation({
    mutationKey: ["assignToUser"],
    mutationFn: async (formData) =>
      await axiosConfig.post("/merchant/add-user", formData),
  });

export const useUnassignFromUser = () =>
  useMutation({
    mutationKey: ["unassignFromUser"],
    mutationFn: async (formData) =>
      await axiosConfig.post("/merchant/add-user/update", formData),
  });

export const useGetBusinessPreferences = (businessId) =>
  useQuery({
    queryKey: ["getBusinessPreferences", businessId],
    queryFn: async () =>
      await axiosConfig.get(`/business/preferences/${businessId}`),
    enabled: !!businessId,
  });

export const useUpdateBusinessPreferences = (businessId) =>
  useMutation({
    mutationKey: ["updateBusinessPreferences", businessId],
    mutationFn: async (preferences) =>
      await axiosConfig.put(`/business/preferences/${businessId}`, preferences),
  });

export const exportReportCSV = async (businessId, metric, startDate, endDate, eventId) => {
  const params = new URLSearchParams({ metric });
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (eventId) params.append("eventId", eventId);

  const response = await axiosConfig.get(
    `/business/report/${businessId}/export/csv?${params.toString()}`,
    { responseType: "blob" }
  );
  return response;
};

export const exportReportPDF = async (businessId, metric, startDate, endDate, eventId) => {
  const params = new URLSearchParams();
  if (metric) params.append("metric", metric);
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (eventId) params.append("eventId", eventId);

  const response = await axiosConfig.get(
    `/business/report/${businessId}/export/pdf?${params.toString()}`
  );
  return response;
};
